import { z } from 'zod'

/**
 * AddressFinder AU wire types — hand-written Zod schemas matching the raw
 * AddressFinder API responses. Required fields are modelled as required (not
 * optional), since AF always returns them on successful responses.
 *
 * These are the raw API shapes. App-specific transformed types live in each
 * app's domain layer.
 */

// ---- Location autocomplete ----

export const AFLocationAutocompleteItemSchema = z.object({
  id: z.string(),
  full_location: z.string(),
})

export type AFLocationAutocompleteItem = z.infer<typeof AFLocationAutocompleteItemSchema>

export const AFLocationAutocompleteResponseSchema = z.object({
  completions: z.array(AFLocationAutocompleteItemSchema),
  paid: z.boolean(),
  demo: z.boolean(),
  success: z.boolean(),
})

export type AFLocationAutocompleteResponse = z.infer<typeof AFLocationAutocompleteResponseSchema>

// ---- Location metadata ----

export const AFLocationMetadataResponseSchema = z.object({
  locality_name: z.string(),
  state_territory: z.string(),
  postcode: z.string(),
})

export type AFLocationMetadataResponse = z.infer<typeof AFLocationMetadataResponseSchema>

// ---- Address autocomplete ----
//
// Per AF docs, `address/autocomplete` only documents `completions` + `success` —
// unlike `location/autocomplete` it makes no promise about `paid`/`demo`, so we
// don't model them here.

export const AFAddressAutocompleteItemSchema = z.object({
  id: z.string(),
  full_address: z.string(),
  canonical_address_id: z.string(),
})

export type AFAddressAutocompleteItem = z.infer<typeof AFAddressAutocompleteItemSchema>

export const AFAddressAutocompleteResponseSchema = z.object({
  completions: z.array(AFAddressAutocompleteItemSchema),
  success: z.boolean(),
})

export type AFAddressAutocompleteResponse = z.infer<typeof AFAddressAutocompleteResponseSchema>

// ---- Address metadata ----
//
// `address_line_2` and `full_address` are documented as commonly present but not
// guaranteed (AF returns null when absent), so they're modelled as nullish.

export const AFAddressMetadataSchema = z.object({
  address_line_1: z.string(),
  address_line_2: z.string().nullish(),
  locality_name: z.string(),
  state_territory: z.string(),
  postcode: z.string(),
  full_address: z.string().nullish(),
})

export type AFAddressMetadata = z.infer<typeof AFAddressMetadataSchema>
